<script setup lang="ts">
import { STORYBLOK_TEMPLATE_STORIES } from '#shared/constants/storyblok';
import { SellingMethod } from '~/apiClient';

// COMPOSABLES
const sellingMethod = useSlpSellingMethod();
const { singleSelectedBrandId, suspenseBrands, suspenseModels, suspenseBusAssetsAttributeValues, suspenseAuctionStates, page, itemsPerPage } =
    useSlpParams(sellingMethod);
const { trackViewSaleList, syncTrackingGlobalSales } = useTracking();
const { isLoggedIn } = useAuthUtils();

// DATA
const { suspense, sales, totalItems, isPending, listingResponse, error, refetch } = useSlpData();
const slpSuspense = useSlpSuspenseState();
const { apiOptions } = useStoryblokApiParams();
const slpTemplateSlug = sellingMethod.value === SellingMethod.Auction ? STORYBLOK_TEMPLATE_STORIES.ALP : STORYBLOK_TEMPLATE_STORIES.DSLP;
const { story: slpTemplate } = await useStoryblokAsyncData(slpTemplateSlug, {
    api: apiOptions,
    bridge: {},
});
const slpTemplateContent = computed(() => slpTemplate.value?.content as StoryblokSlpTemplate | undefined);
const beforeContent = computed(() => slpTemplateContent.value?.beforeContent ?? []);
const afterContent = computed(() => slpTemplateContent.value?.afterContent ?? []);

if (import.meta.server) {
    await suspenseBusAssetsAttributeValues();
    sellingMethod.value === SellingMethod.Auction && (await suspenseAuctionStates());
    await suspenseBrands();
    !!singleSelectedBrandId.value && (await suspenseModels());
}

(slpSuspense.value || import.meta.server) && (await suspense());

syncTrackingGlobalSales(sales);

// SEO
useSlpSeoMeta({ totalItems: toRef(() => totalItems.value.current) });

// WATCHERS
useWatchQueryError({ data: listingResponse, error, logMessage: 'Error fetching sales listing' });

// The listing is scoped to the viewer, so signing in or out changes what it should contain.
watch(isLoggedIn, () => refetch());

watch(
    sales,
    () => {
        if (!import.meta.server && !isPending.value) trackViewSaleList(sales.value);
    },
    { immediate: true }
);
</script>

<template>
    <div>
        <SlpHero />
        <StoryblokComponent v-for="b in beforeContent" :key="b._uid" :blok="b" />
        <SlpList :sales="sales" :pagination="{ page, itemsPerPage, total: totalItems.current }" :is-loading="isPending" />
        <StoryblokComponent v-for="b in afterContent" :key="b._uid" :blok="b" />
    </div>
</template>
